import { useState } from 'react'
import { QuestionMarkCircleIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

interface HelpSection {
  label: string
  content: string
}

interface PageHelpProps {
  title: string
  summary: string
  sections: HelpSection[]
  defaultOpen?: boolean
}

/**
 * Collapsible "what this is / how to use it" panel for a page or major surface.
 * Messaging Advisory blue accents. Originally sales-only; promoted to shared/ so
 * any surface (templates, brands, editor) can host workflow help. Re-exported
 * from components/sales/PageHelp for backward compatibility.
 */
export function PageHelp({ title, summary, sections, defaultOpen = false }: PageHelpProps) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="overflow-hidden rounded-xl border border-primary-100 bg-primary-50/40 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-primary-50"
      >
        <div className="flex items-center gap-2.5">
          <QuestionMarkCircleIcon className="h-4 w-4 flex-shrink-0 text-primary-600" />
          <span className="text-xs font-semibold text-primary-800">{title}</span>
          <span className="hidden text-[11px] text-primary-700/70 sm:block">{summary}</span>
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 flex-shrink-0 text-primary-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="border-t border-primary-100 px-4 pb-4 pt-3">
          <p className="mb-3 text-xs leading-5 text-gray-600">{summary}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sections.map((s) => (
              <div key={s.label} className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-primary-500">{s.label}</p>
                <p className="text-xs leading-5 text-gray-600">{s.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface HelpTipProps {
  text: string
}

/**
 * Inline ? icon with a hover/click tooltip. Use next to field labels or section
 * headers. For selectable cards, prefer InfoPopover (sibling-safe, image slot).
 */
export function HelpTip({ text }: HelpTipProps) {
  const [visible, setVisible] = useState(false)

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        onClick={() => setVisible((v) => !v)}
        className="ml-1 inline-flex items-center justify-center rounded-full text-primary-400 hover:text-primary-600 focus:outline-none"
        aria-label="Help"
      >
        <QuestionMarkCircleIcon className="h-3.5 w-3.5" />
      </button>
      {visible && (
        <span className="absolute left-5 top-0 z-50 w-56 rounded-lg border border-primary-100 bg-white px-3 py-2 text-[11px] leading-5 text-gray-600 shadow-lg">
          {text}
          <button
            type="button"
            onClick={() => setVisible(false)}
            className="absolute right-1.5 top-1.5 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
        </span>
      )}
    </span>
  )
}
